import { usePutFrontRichiesteIdModuliId } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { addContextSnapshotToFormValuesFn } from '@framework/common-web/engine/engine-validation/context-snapshot/serializeContextSnapshot.ts';
import { type IEngineValidationContext } from '@framework/common-web/engine/engine-validation/IEngineValidationContext.ts';
import { type IFrontOfficeModule } from '@framework/common-web/frontoffice/module/IFrontOfficeModule.ts';
import { ConfirmUpdateLogic } from '@framework/common-web/schema/modules/utils/confirm-update/ConfirmUpdateLogic.ts';
import { type IEngineSection } from '@framework/engine/schema/sections/IEngineSection.ts';
import { serializeFormSectionsValues } from '@framework/engine/validator/serialize/serializeFormSectionsValues.ts';
import { applyLiveContextTransform } from '@framework/validation/live/applyLiveContextTransform.ts';
import { message } from 'antd';
import isEqual from 'lodash/isEqual';
import { useCallback } from 'react';
import { type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type AnyObject, type ObjectSchema } from 'yup';
import { SubmissionModuleLogic } from '../../submission/submission-module/SubmissionModuleLogic.ts';
import { useSubmissionConfirmUpdate } from './useSubmissionConfirmUpdate.tsx';

export type IOnAfterSave = (
  savedSubmission: RichiestaDTO | undefined | null,
) => Promise<void>;

export type IOnBeforeSave = () => Promise<void>;

/**
 * Azzera lo stato "dirty" del form mantenendo i valori correnti come nuovi
 * default. Usato dopo un salvataggio riuscito e quando non ci sono modifiche,
 * così la guardia di navigazione non segnala modifiche non salvate spurie.
 */
function markFormPristine(methods?: UseFormReturn) {
  methods?.reset(methods.getValues());
}

/**
 * Hook che torna la funzione di "submit" per il salvataggio e
 * completamento di un modulo.
 */
export function useSubmitModuleForm(params: {
  module: IFrontOfficeModule | null | undefined;
  moduleId: string | null | undefined;
  submissionId: string | null | undefined;
  validationSchema: ObjectSchema<AnyObject>;
  validationContext: IEngineValidationContext;
  sections: IEngineSection[];
  onSuccess?: () => Promise<void>;
  onBeforeSave?: IOnBeforeSave;
  onAfterSave?: IOnAfterSave;
}) {
  const {
    validationSchema,
    validationContext,
    module,
    moduleId,
    submissionId,
    sections,
    onSuccess,
    onBeforeSave,
    onAfterSave,
  } = params;

  const confirmUpdate = useSubmissionConfirmUpdate();

  const save = usePutFrontRichiesteIdModuliId();
  const { t } = useTranslation();

  const forceSave = usePutFrontRichiesteIdModuliId({
    request: { headers: { forceSave: true } },
  });

  const saveForm = useCallback(
    async (params: {
      submissionId: string | null | undefined;
      moduleId: string | null | undefined;
      values: any;
      force: boolean;
      methods?: UseFormReturn;
    }) => {
      const { submissionId, moduleId, values, force, methods } = params;

      if (!moduleId) {
        // biome-ignore format: <tbd>
        console.error(`[SubmitModuleForm] Impossibile salvare il modulo: id modulo o id richiesta mancanti`, { moduleId });
        // biome-ignore format: <tbd>
        message.error(t("error-submit-module-form.errore-salvataggio-modulo", 'Errore durante il salvataggio, ID Modulo non presente'));
        return;
      }

      if (!submissionId) {
        // biome-ignore format: <tbd>
        console.error(`[SubmitModuleForm] Impossibile salvare il modulo: id modulo o id richiesta mancanti`, { submissionId });
        // biome-ignore format: <tbd>
        message.error(t("error-submit-module-form.errore-salvataggio-richiesta", 'Errore durante il salvataggio, ID Richiesta non presente'));
        return;
      }

      const saveFn = force ? forceSave : save;

      await onBeforeSave?.();

      // Lo snapshot deve vedere gli stessi semantic fields live usati dal
      // resolver, altrimenti la riapertura readonly leggerebbe valori vecchi.
      const snapshotValidationContext = applyLiveContextTransform(
        validationSchema,
        values,
        validationContext,
      );

      const serializedValues = serializeFormSectionsValues({
        validationSchema,
        values,
        transform: addContextSnapshotToFormValuesFn(
          sections,
          snapshotValidationContext,
        ),
      });

      const result = await saveFn.mutateAsync({
        idRichiesta: submissionId,
        idRichiestaModulo: moduleId,
        data: {
          valoriSezioni: serializedValues,
        },
      });

      markFormPristine(methods);

      await onAfterSave?.(result.richiesta);

      await onSuccess?.();
    },
    [
      forceSave,
      onAfterSave,
      onBeforeSave,
      onSuccess,
      save,
      sections,
      t,
      validationContext,
      validationSchema,
    ],
  );

  const onSubmit = useCallback(
    async (values: any, methods?: UseFormReturn) => {
      // Controllo se il form è dirty prima di salvare
      // in caso sia pulito non salvo
      const initialNormalized = serializeFormSectionsValues({
        validationSchema,
        values: methods?.formState.defaultValues ?? {},
      });
      const currentNormalized = serializeFormSectionsValues({
        validationSchema,
        values: values ?? {},
      });
      const hasChanges =
        // A) Confronto i valori normalizzati: se sono diversi, il form è dirty
        !isEqual(currentNormalized, initialNormalized) ||
        // B) Se il modulo non è mai stato visitato, lo considero dirty a prescindere
        !SubmissionModuleLogic.isVisited(module?.statoValidazione);

      if (!hasChanges) {
        // Nessuna modifica da salvare: non chiamiamo l'API, ma riallineiamo i
        // default del form ai valori correnti. RHF può infatti marcare il form
        // come dirty anche senza differenze reali (es. campi toccati o riordini
        // equivalenti dopo la normalizzazione): senza questo reset la guardia di
        // navigazione mostrerebbe un prompt spurio pur non essendoci nulla da
        // salvare.
        markFormPristine(methods);
        return;
      }

      try {
        await saveForm({
          submissionId,
          moduleId,
          values,
          force: false,
          methods,
        });
      } catch (e: any) {
        if (ConfirmUpdateLogic.needUserConfirm(e)) {
          confirmUpdate({
            differences: e.response.data,
            onOk: async () => {
              await saveForm({
                submissionId,
                moduleId,
                values,
                force: true,
                methods,
              });
            },
          });
          return;
        }

        // biome-ignore format: <tbd>
        console.error(`[SubmitModuleForm] Errore durante il salvataggio dei dati`, { error: e });
        // biome-ignore format: <tbd>
        message.error(getNetworkErrorMessage(e, t("error-submit-module-form.error-saving",'Errore durante il salvataggio')));
        /**
         * Rilanciamo l'eccezione. In questo modo possiamo gestire il fallimento della chiamata nei componenti
         * che richiamano il submit programmaticamente (come `ManagedSubmitButton`). Se non rilanciassimo l'eccezione,
         * in quei componenti la submit verrebbe considerata sempre valida.
         * N.B. Questo significa che questa on submit _non può essere chiamata_ con un onSubmitButton, che in caso di errore
         * porterebbe a un'eccezione non gestita.
         */
        throw e;
      }
    },
    [
      confirmUpdate,
      moduleId,
      saveForm,
      submissionId,
      t,
      validationSchema,
      module?.statoValidazione,
    ],
  );

  return onSubmit;
}
